<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import {
  adjustmentStatusClass,
  adjustmentStatusLabel,
  adjustmentTypeLabel,
  formatMoney,
} from '../utils/format'
import type {
  FinanceAdjustment,
  FinanceAdjustmentStatus,
  FinanceAdjustmentType,
} from '../types/finance'

const { t } = useI18n()
const auth = useAuthStore()
const { listFinanceAdjustments, postFinanceAdjustment, reverseFinanceAdjustment } = useFinanceApi()

const PER_PAGE = 20

const types: FinanceAdjustmentType[] = ['customer_credit', 'write_off', 'debit_note', 'refund']
const statuses: FinanceAdjustmentStatus[] = ['draft', 'posted', 'reversed']

const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('finance_adjustments.manage'))

const rows = ref<FinanceAdjustment[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const busyId = ref('')
const page = ref(1)

const filters = ref({
  adjustment_type: '' as FinanceAdjustmentType | '',
  status: '' as FinanceAdjustmentStatus | '',
  search: '',
})

const visibleRows = computed(() => {
  const term = filters.value.search.trim().toLowerCase()
  if (!term) return rows.value
  return rows.value.filter((row) =>
    [row.adjustment_number, row.agent_name, row.reason, String(row.invoice_id)]
      .filter((value) => value !== null && value !== undefined)
      .some((value) => String(value).toLowerCase().includes(term)),
  )
})

const totalPages = computed(() => Math.max(1, Math.ceil(visibleRows.value.length / PER_PAGE)))
const pagedRows = computed(() =>
  visibleRows.value.slice((page.value - 1) * PER_PAGE, page.value * PER_PAGE),
)

const rangeLabel = computed(() => {
  const total = visibleRows.value.length
  if (!total) return ''
  const from = (page.value - 1) * PER_PAGE + 1
  const to = Math.min(page.value * PER_PAGE, total)
  return t('finance.common.showing', { from, to, total })
})

const draftCount = computed(() => rows.value.filter((row) => row.status === 'draft').length)

async function load() {
  loading.value = true
  error.value = ''
  try {
    rows.value = await listFinanceAdjustments({
      adjustment_type: filters.value.adjustment_type,
      status: filters.value.status,
    })
    page.value = 1
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function post(adjustment: FinanceAdjustment) {
  actionError.value = ''
  actionMessage.value = ''
  if (!window.confirm(t('finance.adjustments.postConfirm'))) return
  busyId.value = String(adjustment.id)
  try {
    await postFinanceAdjustment(adjustment.id)
    actionMessage.value = t('finance.adjustments.posted')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = ''
  }
}

async function reverse(adjustment: FinanceAdjustment) {
  actionError.value = ''
  actionMessage.value = ''
  if (!window.confirm(t('finance.adjustments.reverseConfirm'))) return
  busyId.value = String(adjustment.id)
  try {
    await reverseFinanceAdjustment(adjustment.id)
    actionMessage.value = t('finance.adjustments.reversed')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = ''
  }
}

watch(
  () => [filters.value.adjustment_type, filters.value.status],
  () => {
    void load()
  },
)

watch(
  () => filters.value.search,
  () => {
    page.value = 1
  },
)

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('finance.adjustments.title') }}</h2>
  </div>

  <div class="toolbar">
    <select v-model="filters.adjustment_type" class="filter-select">
      <option value="">{{ t('finance.common.allTypes') }}</option>
      <option v-for="type in types" :key="type" :value="type">
        {{ adjustmentTypeLabel(t, type) }}
      </option>
    </select>
    <select v-model="filters.status" class="filter-select">
      <option value="">{{ t('finance.common.allStatuses') }}</option>
      <option v-for="status in statuses" :key="status" :value="status">
        {{ adjustmentStatusLabel(t, status) }}
      </option>
    </select>
    <input
      v-model="filters.search"
      class="search-input"
      type="search"
      :placeholder="t('finance.common.searchPlaceholder')"
    >
    <span class="spacer" />
    <div class="summary">
      <span><strong>{{ draftCount }}</strong> {{ t('finance.adjustmentStatus.draft') }}</span>
      <span>{{ t('finance.common.rows', { count: visibleRows.length }) }}</span>
    </div>
    <RouterLink v-if="canManage" class="btn-primary" to="/receivables/adjustments/new">
      {{ t('finance.adjustments.newAdjustment') }}
    </RouterLink>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <div v-else-if="visibleRows.length === 0" class="state-message">
    <p>{{ t('finance.adjustments.empty') }}</p>
  </div>

  <template v-else>
    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.adjustments.adjustmentNumber') }}</th>
            <th>{{ t('finance.common.type') }}</th>
            <th>{{ t('finance.common.invoice') }}</th>
            <th>{{ t('finance.common.agent') }}</th>
            <th>{{ t('finance.common.date') }}</th>
            <th class="cell-num">{{ t('finance.common.amount') }}</th>
            <th>{{ t('finance.common.account') }}</th>
            <th>{{ t('finance.common.reason') }}</th>
            <th>{{ t('finance.common.status') }}</th>
            <th class="cell-actions">{{ t('finance.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="adjustment in pagedRows" :key="adjustment.id">
            <td class="cell-code">{{ adjustment.adjustment_number }}</td>
            <td>{{ adjustmentTypeLabel(t, adjustment.adjustment_type) }}</td>
            <td>
              <RouterLink class="link-action" :to="`/receivables/invoices/${adjustment.invoice_id}`">
                #{{ adjustment.invoice_id }}
              </RouterLink>
              <span v-if="adjustment.invoice_amount !== null && adjustment.invoice_amount !== undefined" class="cell-sub">
                {{ formatMoney(adjustment.invoice_amount, adjustment.currency_code) }}
              </span>
            </td>
            <td class="cell-name">{{ adjustment.agent_name || adjustment.agent_id || '—' }}</td>
            <td class="cell-date">{{ formatDisplayDate(adjustment.adjustment_date) }}</td>
            <td class="cell-num">
              {{ formatMoney(adjustment.amount, adjustment.currency_code) }}
            </td>
            <td>{{ adjustment.financial_account_name || '—' }}</td>
            <td>{{ adjustment.reason }}</td>
            <td>
              <span class="status-badge" :class="adjustmentStatusClass(adjustment.status)">
                {{ adjustmentStatusLabel(t, adjustment.status) }}
              </span>
            </td>
            <td class="cell-actions">
              <button
                v-if="canManage && adjustment.status === 'draft'"
                class="link-action"
                :disabled="busyId === String(adjustment.id)"
                @click="post(adjustment)"
              >
                {{ t('finance.adjustments.post') }}
              </button>
              <button
                v-if="canManage && adjustment.status === 'posted'"
                class="link-action danger"
                :disabled="busyId === String(adjustment.id)"
                @click="reverse(adjustment)"
              >
                {{ t('finance.adjustments.reverse') }}
              </button>
              <span
                v-if="!canManage || adjustment.status === 'reversed'"
                class="empty-inline"
              >—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <span>{{ rangeLabel }}</span>
      <button :disabled="page <= 1" @click="page -= 1">{{ t('finance.common.previous') }}</button>
      <span>{{ t('finance.common.page', { page, total: totalPages }) }}</span>
      <button :disabled="page >= totalPages" @click="page += 1">
        {{ t('finance.common.next') }}
      </button>
    </div>
  </template>
</template>
